import React, { useEffect, useState } from 'react';

import IconChevron from '$partials/icons/icon-chevron.jsx';
import IconLogo from '$partials/icons/icon-logo.jsx';
import GeneralEnquiryDialog from './general-enquiry-dialog';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Header}
 */
const Header = ({
    variant = 'DEFAULT',
    primaryMenu,
    secondaryMenu,
    showLogo = true,
    defaultOffice
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [openSubmenuId, setOpenSubmenuId] = useState(null);

    // Toggle a specific submenu open/closed by ID. Tapping the same button
    // again closes it; tapping a different button switches to that one.

    const handleSubmenuToggle = (id) => () => {
        setOpenSubmenuId((current) => current === id ? null : id);
    };

    // Close the open submenu when the user taps/clicks anywhere outside it.
    // The listener is only registered while a submenu is open.
    useEffect(() => {
        if (!openSubmenuId) { return; }
        const closeOnOutsideClick = (e) => {
            if (!(e.target).closest('.header__submenu')) {
                setOpenSubmenuId(null);
            }
        };
        document.addEventListener('click', closeOnOutsideClick);
        return () => document.removeEventListener('click', closeOnOutsideClick);
    }, [openSubmenuId]);

    const logoHidden = Boolean(variant === 'HOME' && !showLogo);

    return (
        <>
            <header className={`header type-style-eyebrow ${variant ? ` header--${variant.toLowerCase()}` : ''}`}>
                {/* Hover / focus background */}
                <div className='header__hover-background' />

                <nav
                    aria-label='Primary Navigation'
                    className='header__menu-container'
                    id='primary-navigation'>
                    <ul className={`layout-container header__menu${logoHidden ? ' header__menu--nologo' : ''}`}>
                        {/* Logo */}
                        <li className={`header__logo-container${logoHidden ? ' !hidden' : ''}`}>
                            <a
                                aria-label='Homepage'
                                className='h-[58px] flex items-center self-start px-layout-outer'
                                href='/'>
                                <IconLogo className='w-[169px]' />
                            </a>
                        </li>

                        {/* Primary menu */}
                        {(primaryMenu?.length > 0) &&
                            <li className='header__menu-item order-last sm:order-1 flex-grow sm:flex-grow-0 flex justify-end sm:justify-start'>
                                <div className={`header__submenu${openSubmenuId === 'menu' ? ' header__submenu--open' : ''}`}>
                                    <button
                                        className='header__submenu-toggle'
                                        id='header-submenu-heading-menu'
                                        onClick={handleSubmenuToggle('menu')}
                                        type='button'>
                                        Menu
                                        <IconChevron className='header__submenu-toggle-icon' />
                                    </button>
                                    <div className='header__submenu-menu w-full sm:w-auto'>
                                        <ul
                                            aria-labelledby='header-submenu-heading-menu'
                                            className='relative flex flex-col gap-y-8 py-20 sm:px-0 type-style-body-l'>
                                            {primaryMenu.map((item, i) => {
                                                return (
                                                    <a
                                                        {...item.link}
                                                        className='px-20'
                                                        key={i}>
                                                        {item.label}
                                                    </a>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            </li>
                        }

                        {/* Secondary menu */}
                        {secondaryMenu?.map((item, i) => {
                            let responsiveClassName;
                            if (i > 2) { responsiveClassName = 'hidden md:block'; }
                            else if (i > 1) { responsiveClassName = 'hidden sm:block'; }
                            if (item.__typename === 'SubMenuBlock') {
                                return (
                                    <li
                                        className={`header__menu-item sm:order-2${responsiveClassName ? ` ${responsiveClassName}` : ''}`}
                                        key={i}>
                                        <div className={`header__submenu${openSubmenuId === `secondary-${i}` ? ' header__submenu--open' : ''}`}>
                                            <button
                                                className='header__submenu-toggle'
                                                id={`header-submenu-heading-${i}`}
                                                onClick={handleSubmenuToggle(`secondary-${i}`)}
                                                type='button'>
                                                {item.heading}
                                                <IconChevron className='header__submenu-toggle-icon' />
                                            </button>
                                            <div className='header__submenu-menu'>
                                                <ul
                                                    aria-labelledby={`header-submenu-heading-${i}`}
                                                    className='relative flex flex-col gap-y-8 py-20 sm:px-0 type-style-body-l'>
                                                    {item.items.map((item, i) => {
                                                        return (
                                                            <li key={i}>
                                                                <a
                                                                    {...item.link}
                                                                    className='px-20'>
                                                                    {item.label}
                                                                </a>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    </li>
                                );
                            } else if (item.__typename === 'MenuLinkBlock') {
                                return (
                                    <li
                                        className={`sm:order-2${responsiveClassName ? ` ${responsiveClassName}` : ''}`}
                                        key={i}>
                                        <a
                                            {...item.link}
                                            className='header__menu-item px-20'>
                                            {item.label}
                                        </a>
                                    </li>
                                );
                            }
                        })}

                        {/* Contact */}
                        <li className='hidden sm:flex sm:order-last flex-grow justify-end'>
                            <button
                                className='header__menu-item pl-20 pr-layout-outer type-style-eyebrow'
                                onClick={() => setIsDialogOpen(true)}
                                type='button'>
                                {'Contact'}
                            </button>
                        </li>
                    </ul>
                </nav>
            </header>

            {/* Background for PDP variant */}
            {(variant === 'PDP') &&
                <div className='header__background' />
            }

            {isDialogOpen && (
                <GeneralEnquiryDialog
                    closeDialog={() => setIsDialogOpen(false)}
                    defaultOffice={defaultOffice}
                    isOpen={isDialogOpen} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Header;
